import React from "react";
import { GeoJSON, Popup } from "react-leaflet";
import { Link } from "react-router-dom";
import { bestWildcadMatch } from "../../lib/dedupe";

const STYLE = {
  color: "#E63946",
  weight: 2,
  opacity: 0.9,
  fillColor: "#E63946",
  fillOpacity: 0.18,
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const FLASH_STYLE = {
  color: "#FFD60A",
  weight: 4,
  opacity: 1,
  fillColor: "#E63946",
  fillOpacity: 0.32,
  className: "perimeter-flash",
};

function PerimeterLayer({ perimeters, flashId = null, linkIncidents = [] }) {
  return perimeters.map((p) => {
    const flashing = flashId != null && p.id === flashId;
    const wcLink = bestWildcadMatch(linkIncidents, {
      name: p.name,
      latitude: p.centroid_lat ?? p.latitude,
      longitude: p.centroid_lon ?? p.longitude,
    });
    return (
      <GeoJSON
        key={`perim-${p.id}-${flashing ? "flash" : "base"}`}
        data={{ type: "Feature", geometry: p.geometry, properties: {} }}
        style={flashing ? FLASH_STYLE : STYLE}
      >
        <Popup>
          <div className="min-w-[220px]" data-testid={`perimeter-popup-${p.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-tac-danger mb-1">
              NIFC · Active Fire Perimeter
            </div>
            <div className="font-display font-bold text-sm text-tac-text mb-1 leading-snug">
              {p.name}
            </div>
            <div className="text-[14px] text-tac-dim font-mono-tac tabular space-y-0.5">
              {p.incident_size != null && (
                <div>{Math.round(p.incident_size).toLocaleString()} ac official</div>
              )}
              {p.acres != null &&
                (p.incident_size == null ||
                  Math.round(p.acres) !== Math.round(p.incident_size)) && (
                  <div>{Math.round(p.acres).toLocaleString()} ac mapped perimeter</div>
                )}
              {p.percent_contained != null && (
                <div>{Math.round(p.percent_contained)}% contained</div>
              )}
              {p.cause && <div>Cause: {p.cause}</div>}
              {p.county && <div>{p.county} County{p.state ? `, ${p.state.replace("US-", "")}` : ""}</div>}
              {p.protecting_agency && <div>Protecting: {p.protecting_agency}</div>}
              {p.discovered_at && <div>Discovered {fmtDate(p.discovered_at)}</div>}
              {(p.modified_at || p.updated_at) && (
                <div>Updated {fmtDate(p.modified_at || p.updated_at)}</div>
              )}
            </div>
            {p.short_description && (
              <div className="text-[11px] text-tac-text mt-2 leading-snug">
                {p.short_description}
              </div>
            )}
            {wcLink && (
              <Link
                to={`/incident/${wcLink.id}`}
                data-testid={`perimeter-popup-details-${p.id}`}
                className="inline-block mt-2 text-[11px] font-bold uppercase tracking-wider hover:underline text-tac-danger"
              >
                View details →
              </Link>
            )}
            <div className="text-[10px] font-mono-tac text-tac-faint mt-2 pt-1 border-t border-tac-border/60">
              Source: NIFC WFIGS · {p.unique_fire_id || p.irwin_id?.slice(1, 9) || p.id.slice(0, 8)}
            </div>
          </div>
        </Popup>
      </GeoJSON>
    );
  });
}

export default React.memo(PerimeterLayer);
